import React from 'react';
import { Mail, Phone, Smartphone, Wifi } from 'lucide-react';

const CardPreview = ({ customization, productType }) => {
  const { 
    name, 
    title, 
    company, 
    phone, 
    email, 
    website, 
    cardColor, 
    accentColor, 
    textColor, 
    logoPreview,
    template,
    font
  } = customization;

  const fontFamilies = {
    inter: "'Inter', sans-serif",
    poppins: "'Poppins', sans-serif",
    roboto: "'Roboto', sans-serif",
    playfair: "'Playfair Display', serif",
    montserrat: "'Montserrat', sans-serif"
  };

  if (productType === 'keychain') {
    return (
      <div className="relative" style={{ perspective: '1000px' }}>
        <div className="bg-gradient-to-br from-zinc-900 to-black p-8 rounded-2xl border border-zinc-800">
          {/* Keychain Preview */}
          <div className="flex items-center justify-center">
            <div className="relative">
              {/* Keychain Ring */}
              <div className="absolute -top-8 left-1/2 -translate-x-1/2">
                <div className="w-12 h-12 rounded-full border-4 border-zinc-700"></div>
              </div>
              
              {/* Keychain Tag */}
              <div 
                className="w-64 h-80 rounded-2xl shadow-2xl relative overflow-hidden"
                style={{ 
                  backgroundColor: cardColor,
                  transform: 'rotateY(-5deg) rotateX(5deg)',
                  fontFamily: fontFamilies[font]
                }}
              >
                {/* Accent Strip */}
                <div 
                  className="h-24 w-full"
                  style={{ backgroundColor: accentColor }}
                />
                
                {/* Content */}
                <div className="p-6 space-y-4">
                  {logoPreview && (
                    <div className="flex justify-center mb-4">
                      <img src={logoPreview} alt="Logo" className="h-12 object-contain" />
                    </div>
                  )}
                  
                  <div className="text-center">
                    <h3 className="text-xl font-bold mb-2" style={{ color: textColor }}>
                      {name}
                    </h3>
                    <p className="text-sm mb-1" style={{ color: accentColor }}>
                      {title}
                    </p>
                    <p className="text-xs opacity-75" style={{ color: textColor }}>
                      {company}
                    </p>
                  </div>

                  <div className="pt-4 border-t border-white/10 space-y-2">
                    <div className="flex items-center justify-center gap-2 text-xs" style={{ color: textColor }}>
                      <Wifi className="w-4 h-4" style={{ color: accentColor }} />
                      <span>Tap to Connect</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <p className="text-center text-gray-400 mt-6 text-sm">
            Actual size: 3.5" x 2" (Credit card size with keyring)
          </p>
        </div>
      </div>
    );
  }

  // Business Card Templates
  const renderModernTemplate = () => (
    <div className="h-full p-8 flex flex-col justify-between">
      {/* Top Section */}
      <div>
        {logoPreview && (
          <div className="mb-6">
            <img src={logoPreview} alt="Logo" className="h-10 object-contain" />
          </div>
        )}
        
        <div>
          <h3 className="text-2xl font-bold mb-2" style={{ color: textColor }}>
            {name}
          </h3>
          <p className="text-base mb-1" style={{ color: accentColor }}>
            {title}
          </p>
          <p className="text-sm opacity-75" style={{ color: textColor }}>
            {company}
          </p>
        </div>
      </div>

      {/* Bottom Section */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm" style={{ color: textColor }}>
          <Phone className="w-4 h-4" style={{ color: accentColor }} />
          <span>{phone}</span>
        </div>
        <div className="flex items-center gap-2 text-sm" style={{ color: textColor }}>
          <Mail className="w-4 h-4" style={{ color: accentColor }} />
          <span className="truncate">{email}</span>
        </div>
        <div className="flex items-center gap-2 text-sm" style={{ color: textColor }}>
          <Smartphone className="w-4 h-4" style={{ color: accentColor }} />
          <span>{website}</span>
        </div>
      </div>

      {/* NFC Indicator */}
      <div className="absolute bottom-4 right-4">
        <div 
          className="w-12 h-12 rounded-full flex items-center justify-center"
          style={{ backgroundColor: accentColor + '20' }}
        >
          <Wifi className="w-6 h-6" style={{ color: accentColor }} />
        </div>
      </div>
    </div>
  );

  const renderCorporateTemplate = () => (
    <div className="h-full flex">
      {/* Left Accent Bar */}
      <div className="w-1/3" style={{ backgroundColor: accentColor }}>
        <div className="h-full flex flex-col items-center justify-center p-6 text-center">
          {logoPreview && (
            <img src={logoPreview} alt="Logo" className="h-16 mb-6 object-contain brightness-0 invert" />
          )}
          <Wifi className="w-12 h-12 text-white mb-4" />
          <p className="text-white text-xs font-semibold">NFC ENABLED</p>
        </div>
      </div>

      {/* Right Content */}
      <div className="flex-1 p-8 flex flex-col justify-between">
        <div>
          <h3 className="text-2xl font-bold mb-2" style={{ color: textColor }}>
            {name}
          </h3>
          <p className="text-base mb-4" style={{ color: accentColor }}>
            {title}
          </p>
        </div>

        <div>
          <p className="text-sm font-semibold mb-3" style={{ color: textColor }}>
            {company}
          </p>
          <div className="space-y-1.5 text-xs" style={{ color: textColor, opacity: 0.8 }}>
            <p>{phone}</p>
            <p>{email}</p>
            <p>{website}</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderCreativeTemplate = () => (
    <div className="h-full relative overflow-hidden">
      {/* Diagonal Background */}
      <div 
        className="absolute inset-0"
        style={{
          background: `linear-gradient(135deg, ${cardColor} 0%, ${cardColor} 60%, ${accentColor} 60%, ${accentColor} 100%)`
        }}
      />

      <div className="relative h-full p-8 flex flex-col justify-between">
        <div>
          {logoPreview && (
            <div className="mb-6">
              <img src={logoPreview} alt="Logo" className="h-12 object-contain" />
            </div>
          )}
          
          <h3 className="text-3xl font-bold mb-2" style={{ color: textColor }}>
            {name}
          </h3>
          <p className="text-lg mb-1 font-semibold" style={{ color: accentColor === cardColor ? textColor : '#000' }}>
            {title}
          </p>
        </div>

        <div>
          <p className="text-sm font-bold mb-3" style={{ color: textColor }}>
            {company}
          </p>
          <div className="space-y-1 text-xs" style={{ color: textColor, opacity: 0.9 }}>
            <p>{phone}</p>
            <p>{email}</p>
            <p>{website}</p>
          </div>
        </div>

        <div className="absolute top-4 right-4">
          <Wifi className="w-8 h-8" style={{ color: textColor, opacity: 0.5 }} />
        </div>
      </div>
    </div>
  );

  const renderElegantTemplate = () => (
    <div className="h-full p-8 flex flex-col items-center justify-center text-center relative">
      {logoPreview && (
        <div className="mb-6">
          <img src={logoPreview} alt="Logo" className="h-14 object-contain mx-auto" />
        </div>
      )}

      <div className="mb-6">
        <h3 className="text-3xl font-bold mb-3" style={{ color: textColor }}>
          {name}
        </h3>
        <div 
          className="w-24 h-0.5 mx-auto mb-3"
          style={{ backgroundColor: accentColor }}
        />
        <p className="text-base mb-2" style={{ color: accentColor }}>
          {title}
        </p>
        <p className="text-sm opacity-75" style={{ color: textColor }}>
          {company}
        </p>
      </div>

      <div className="space-y-2 text-sm" style={{ color: textColor }}>
        <p>{phone}</p>
        <p>{email}</p>
        <p>{website}</p>
      </div>

      <div className="absolute bottom-6">
        <div className="flex items-center gap-2 text-xs" style={{ color: accentColor }}>
          <Wifi className="w-4 h-4" />
          <span>NFC Enabled</span>
        </div>
      </div>
    </div>
  );

  const templates = {
    modern: renderModernTemplate,
    corporate: renderCorporateTemplate,
    creative: renderCreativeTemplate,
    elegant: renderElegantTemplate
  };

  return (
    <div className="relative" style={{ perspective: '1000px' }}>
      <div className="bg-gradient-to-br from-zinc-900 to-black p-8 rounded-2xl border border-zinc-800">
        {/* Card Preview with 3D Effect */}
        <div className="flex items-center justify-center mb-6">
          <div 
            className="w-[420px] h-[252px] rounded-2xl shadow-2xl relative transition-all duration-500 hover:scale-105"
            style={{ 
              backgroundColor: cardColor,
              transform: 'rotateY(-5deg) rotateX(5deg)',
              boxShadow: `0 25px 50px -12px ${accentColor}40`,
              fontFamily: fontFamilies[font]
            }}
          >
            {templates[template]()}
          </div>
        </div>

        {/* Card Back Preview */}
        <div className="flex items-center justify-center">
          <div 
            className="w-[420px] h-[252px] rounded-2xl shadow-xl relative"
            style={{ 
              backgroundColor: cardColor,
              transform: 'rotateY(5deg) rotateX(-3deg) scale(0.85)',
              boxShadow: `0 10px 30px -12px ${accentColor}20`,
              fontFamily: fontFamilies[font]
            }}
          >
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <div 
                  className="w-24 h-24 rounded-full mx-auto mb-4 flex items-center justify-center"
                  style={{ backgroundColor: accentColor + '20' }}
                >
                  <Wifi className="w-12 h-12" style={{ color: accentColor }} />
                </div>
                <p className="text-sm font-semibold" style={{ color: textColor }}>
                  Tap to Connect
                </p>
                <p className="text-xs mt-2 opacity-75" style={{ color: textColor }}>
                  NFC Enabled Digital Business Card
                </p>
              </div>
            </div>
          </div>
        </div>

        <p className="text-center text-gray-400 mt-6 text-sm">
          Actual size: 3.5" x 2" (Standard business card)
        </p>
      </div>
    </div>
  );
};

export default CardPreview;
